import { ReactNode } from 'react';
import { Box, IconButton, Stack, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import { ReactionCounts, ReactionKind, ReactionUserNames } from '../../Interfaces/IEventInteraction';
import { reactionKindLabel } from '../../hooks/eventInteractions';

interface ReactionBarProps {
    counts: ReactionCounts;
    userNamesByReaction?: ReactionUserNames;
    viewerEmoji: ReactionKind | null;
    disabled?: boolean;
    onToggle: (kind: ReactionKind) => void;
}

const ALL_REACTIONS: ReactionKind[] = ['❤️', '👍', '🍺'];

const renderTooltipTitle = (kind: ReactionKind, userNames: string[]): string | ReactNode => {
    if (userNames.length === 0) {
        return `Noch keine ${reactionKindLabel[kind]}-Reaktionen`;
    }

    return (
        <Box sx={{ minWidth: 140 }}>
            <Typography component="div" variant="caption" sx={{ fontWeight: 700, mb: 0.5 }}>
                {reactionKindLabel[kind]}
            </Typography>
            <Stack spacing={0.25}>
                {userNames.map((userName) => (
                    <Typography key={`${kind}-${userName}`} component="div" variant="caption">
                        {userName}
                    </Typography>
                ))}
            </Stack>
        </Box>
    );
};

const ReactionBar: React.FC<ReactionBarProps> = ({
    counts,
    userNamesByReaction,
    viewerEmoji,
    disabled = false,
    onToggle,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    return (
        <Stack direction="row" spacing={0.5} sx={{ justifyContent: 'flex-end', flexWrap: 'nowrap', flexShrink: 0 }}>
            {ALL_REACTIONS.map((kind) => {
                const count = counts[kind];
                const reactedByMe = viewerEmoji === kind;
                const userNames = userNamesByReaction?.[kind] ?? [];

                return (
                    <Box key={kind} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
                        <Tooltip title={renderTooltipTitle(kind, userNames)} arrow enterDelay={150}>
                            <IconButton
                                size="small"
                                disabled={disabled}
                                onClick={() => onToggle(kind)}
                                aria-label={`${reactionKindLabel[kind]} reagieren`}
                                sx={{
                                    width: isMobile ? 24 : 28,
                                    height: isMobile ? 24 : 28,
                                    p: isMobile ? 0.25 : 0.4,
                                    border: '1px solid',
                                    borderColor: reactedByMe ? 'text.primary' : 'divider',
                                    bgcolor: 'transparent',
                                    color: 'inherit',
                                    opacity: 1,
                                    transition: 'border-color 120ms ease',
                                    '&:hover': {
                                        bgcolor: 'transparent',
                                        borderColor: reactedByMe ? 'text.primary' : 'text.secondary',
                                    },
                                    '&.Mui-disabled': {
                                        bgcolor: 'transparent',
                                        color: 'inherit',
                                        opacity: 1,
                                        borderColor: 'divider',
                                    },
                                }}
                            >
                                <Typography component="span" sx={{ fontSize: isMobile ? 12 : 14, lineHeight: 1 }}>
                                    {reactionKindLabel[kind]}
                                </Typography>
                            </IconButton>
                        </Tooltip>
                        <Typography
                            variant="caption"
                            color={reactedByMe ? 'primary.main' : 'text.secondary'}
                            sx={{ minWidth: 8, fontWeight: reactedByMe ? 700 : 500, lineHeight: 1 }}
                        >
                            {count}
                        </Typography>
                    </Box>
                );
            })}
        </Stack>
    );
};

export default ReactionBar;
